import React from "react";

/**
 * Subtile Jugendstil-Ornamente inspirerade av Alphonse Mucha.
 * Används som diskreta avdelare och hörndekorationer.
 * Håll opaciteten låg (0.12-0.3) så att Folkhem-känslan bevaras.
 */

/** Horisontell art nouveau-ornament (linjer + blomma i mitten). */
export function OrnamentDivider({ className = "", color = "currentColor" }) {
  return (
    <div className={`flex items-center justify-center ${className}`} aria-hidden="true">
      <svg width="260" height="24" viewBox="0 0 260 24" xmlns="http://www.w3.org/2000/svg">
        <g fill="none" stroke={color} strokeWidth="1" strokeLinecap="round">
          {/* vänster linje + böljor */}
          <path d="M4 12 L90 12" />
          <path d="M60 12 Q70 4 80 12 Q90 20 100 12" strokeWidth="0.8" />
          {/* höger linje + böljor */}
          <path d="M170 12 L256 12" />
          <path d="M160 12 Q170 4 180 12 Q190 20 200 12" strokeWidth="0.8" />
        </g>
        {/* Central stylized flower */}
        <g transform="translate(130 12)" fill="none" stroke={color} strokeWidth="1" strokeLinejoin="round">
          <circle r="3" fill={color} fillOpacity="0.12" />
          <circle r="1.2" fill={color} />
          <path d="M0 -8 Q-3 -4 0 0 Q3 -4 0 -8 Z" />
          <path d="M0 8 Q-3 4 0 0 Q3 4 0 8 Z" />
          <path d="M-8 0 Q-4 -3 0 0 Q-4 3 -8 0 Z" />
          <path d="M8 0 Q4 -3 0 0 Q4 3 8 0 Z" />
        </g>
      </svg>
    </div>
  );
}

/** Rankor-ornament på höger sida (används i hero/headers). */
export function VineOrnament({ className = "", color = "currentColor", flip = false }) {
  return (
    <svg
      className={className}
      viewBox="0 0 80 200"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={flip ? { transform: "scaleX(-1)" } : undefined}
    >
      <g fill="none" stroke={color} strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round">
        {/* huvudranka */}
        <path d="M10 0 C 20 40, 50 60, 30 100 S 10 160, 40 200" />
        {/* blad */}
        <path d="M30 28 Q 48 32 42 52 Q 24 48 30 28 Z" fill={color} fillOpacity="0.08" />
        <path d="M48 82 Q 66 88 58 108 Q 42 100 48 82 Z" fill={color} fillOpacity="0.08" />
        <path d="M24 132 Q 6 136 14 158 Q 30 152 24 132 Z" fill={color} fillOpacity="0.08" />
        {/* blommor */}
        <g transform="translate(56 54)">
          <circle r="4.5" fill={color} fillOpacity="0.15" />
          <circle r="1.6" fill={color} />
        </g>
        <g transform="translate(18 178)">
          <circle r="3.5" fill={color} fillOpacity="0.15" />
          <circle r="1.2" fill={color} />
        </g>
      </g>
    </svg>
  );
}

/** Mucha-inspirierad rose/rosette – dekorativt mittelementdärrt. */
export function MuchaRose({ className = "", size = 64, color = "currentColor" }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <g fill="none" stroke={color} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
        {/* ytter circle */}
        <circle cx="32" cy="32" r="28" strokeWidth="0.7" />
        <circle cx="32" cy="32" r="22" strokeWidth="0.5" opacity="0.55" />
        {/* 8 kronblad runt centrum */}
        {[0, 45, 90, 135, 180, 225, 270, 315].map(a => (
          <g key={a} transform={`rotate(${a} 32 32)`}>
            <path d="M32 10 Q 27 18 32 26 Q 37 18 32 10 Z" fill={color} fillOpacity="0.12" />
          </g>
        ))}
        {/* inre blomma */}
        <circle cx="32" cy="32" r="6" fill={color} fillOpacity="0.18" />
        <circle cx="32" cy="32" r="2" fill={color} />
        {/* tunn ranka-detalj */}
        {[0, 90, 180, 270].map(a => (
          <g key={a} transform={`rotate(${a} 32 32)`}>
            <path d="M32 4 L 32 8" strokeWidth="0.8" />
            <circle cx="32" cy="6" r="0.8" fill={color} />
          </g>
        ))}
      </g>
    </svg>
  );
}

export function CornerFlourish({ className = "", color = "currentColor", corner = "tl" }) {
  const rotation = {
    tl: 0, tr: 90, br: 180, bl: 270,
  }[corner];
  return (
    <svg
      className={className}
      width="36" height="36" viewBox="0 0 36 36"
      xmlns="http://www.w3.org/2000/svg"
      style={{ transform: `rotate(${rotation}deg)` }}
      aria-hidden="true"
    >
      <g fill="none" stroke={color} strokeWidth="1" strokeLinecap="round">
        <path d="M4 4 L16 4" />
        <path d="M4 4 L4 16" />
        <path d="M4 4 Q 12 12 20 4 Q 12 12 4 20" strokeWidth="0.8" />
        <circle cx="4" cy="4" r="1.6" fill={color} />
      </g>
    </svg>
  );
}

/**
 * Tre silverlaxar (Ångermanlands landskapsvapen, heraldisk stilisering).
 * Använd nära Fraktur-rubriker eller som hjälteaccent. Håll färg silver/blå.
 */
export function LaxOrnament({ className = "", color = "currentColor", count = 3 }) {
  const positions = count === 3 ? [0, 18, 36] : [0, 18];
  return (
    <svg
      className={className}
      viewBox={`0 0 80 ${Math.max(54, positions[positions.length - 1] + 18)}`}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <g fill="none" stroke={color} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
        {positions.map((y, i) => (
          <g key={i} transform={`translate(4 ${y + 9})`}>
            {/* kropp */}
            <path d="M0 0 Q 18 -6 52 0 Q 54 1 52 2 Q 18 8 0 2 Z" fill={color} fillOpacity="0.14" />
            {/* ryggfena */}
            <path d="M28 -4 Q 32 -10 38 -3" />
            {/* analfena */}
            <path d="M30 5 Q 33 9 38 5" />
            {/* stjärtfena */}
            <path d="M52 0 L 62 -5 L 58 1 L 62 6 Z" fill={color} fillOpacity="0.18" />
            {/* öga */}
            <circle cx="10" cy="0" r="1" fill={color} />
            {/* gäl-linje */}
            <path d="M16 -2 Q 18 0 16 2" strokeWidth="0.7" />
          </g>
        ))}
      </g>
    </svg>
  );
}

/**
 * Styvmorsviol (Viola tricolor) – Ångermanlands landskapsblomma.
 * Stiliserad Jugendstil-vignette, 5 kronblad med mittöga.
 */
export function StyvmorsviolOrnament({ className = "", color = "currentColor" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <g transform="translate(24 24)">
        {/* 5 kronblad */}
        <g fill={color} fillOpacity="0.18" stroke={color} strokeWidth="0.8" strokeLinejoin="round">
          {/* topp */}
          <path d="M0 -18 Q -7 -14 -5 -4 Q 0 -2 5 -4 Q 7 -14 0 -18 Z" />
          {/* övre höger */}
          <path d="M17 -8 Q 12 -14 5 -8 Q 3 -3 7 0 Q 14 -1 17 -8 Z" />
          {/* övre vänster */}
          <path d="M-17 -8 Q -12 -14 -5 -8 Q -3 -3 -7 0 Q -14 -1 -17 -8 Z" />
          {/* nedre höger */}
          <path d="M14 10 Q 18 4 10 0 Q 5 1 3 5 Q 6 12 14 10 Z" />
          {/* nedre vänster */}
          <path d="M-14 10 Q -18 4 -10 0 Q -5 1 -3 5 Q -6 12 -14 10 Z" />
        </g>
        {/* mittöga */}
        <circle r="3" fill={color} fillOpacity="0.4" />
        <circle r="1.3" fill={color} />
        {/* finstreckning i nedre kronblad (stilisering av violens ådror) */}
        <g fill="none" stroke={color} strokeWidth="0.5" strokeOpacity="0.6">
          <path d="M-2 4 Q -6 7 -10 6" />
          <path d="M2 4 Q 6 7 10 6" />
          <path d="M0 4 Q 0 9 0 11" />
        </g>
      </g>
    </svg>
  );
}

